<template>
    <div class="base-select">
        <div
            v-if="$slots && $slots.label"
            class="select-label"
        >
            <slot name="label" />
        </div>
        <div>
            <el-select
                :filterable="filterable"
                :collapse-tags="collapseTags"
                :clearable="multiple || clearable"
                :multiple="multiple"
                :value="modelLocal"
                :loading="loading"
                :placeholder="placeholder"
                :popper-append-to-body="appendToBody"
                :size="size"
                :class="{'w-full': fullWidth}"
                data-testid="input-select"
                v-bind="$attrs"
                v-on="listeners"
                @change="onChange"
            >
                <template #prefix>
                    <slot name="prefix" />
                </template>
                <slot>
                    <el-option
                        v-if="multiple && data.length"
                        :label="$t('select.all')"
                        :disabled="allValuesSelected"
                        value="all"
                    />
                    <el-option
                        v-if="multiple && data.length"
                        :label="$t('select.clear')"
                        :disabled="noValueSelected"
                        value="none"
                    />
                    <el-option
                        v-for="(option, key) in data"
                        :key="key"
                        :label="composeLabel(option)"
                        :value="getOptionValue(option)"
                    >
                        <slot
                            name="option-item"
                            :option="option"
                        >
                            <div
                                :key="key"
                                class="flex items-center"
                            >
                                <div
                                    v-if="!showTooltip"
                                    class="flex justify-center items-center"
                                >
                                    <component
                                        :is="option.icon"
                                        v-if="option.icon"
                                        class="text-primary w-4 h-4 ltr:mr-1 rtl:ml-1"
                                    />
                                    {{ composeLabel(option) }}
                                </div>
                                <div v-else-if="showTooltip && customTooltip">
                                    <div class="flex">
                                        <redesign-tooltip placement="left">
                                            <template #tooltip-content>
                                                <div class="custom-tooltip-content">
                                                    <slot
                                                        name="option-tooltip"
                                                        :option="option"
                                                    />
                                                </div>
                                            </template>

                                            <div>
                                                <i
                                                    class="vc-icon-info text-primary cursor-help icon-base my-auto"
                                                    :class="isRTL ? 'ml-2' : 'mr-2'"
                                                />
                                            </div>
                                        </redesign-tooltip>
                                        <div>
                                            {{ composeLabel(option) }}
                                        </div>
                                    </div>
                              </div>
                                <div v-else>
                                    <el-tooltip
                                        placement="right"
                                        :open-delay="400"
                                    >
                                        <template #content>
                                            <div>
                                                {{ option.email }}
                                            </div>
                                        </template>
                                        <div>
                                            <component
                                                :is="option.icon"
                                                v-if="option.icon"
                                                class="text-primary w-4 h-4 ltr:mr-1 rtl:ml-1"
                                            />
                                            {{ composeLabel(option) }}
                                        </div>
                                    </el-tooltip>
                                </div>
                            </div>
                        </slot>
                    </el-option>
                </slot>
                <slot name="custom-value" />
            </el-select>
        </div>
    </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Option as ElOption, Select as ElSelect, Tooltip as ElTooltip } from 'element-ui'
import { computed, nextTick, useListeners } from 'vue'
import RedesignTooltip from '@/components/RedesignBaseComponents/RedesignTooltip.vue'
import { isRTL } from '@/composables/useDirection'

type ObjectAnyType = { [key: string]: any | undefined }
type ValueType = any
type ConfigType = {
    valueKey?: string
    labelKey?: string
    secondLabelKey?: string
}

const listeners = useListeners()

interface IProps {
    collapseTags?: boolean
    filterable?: boolean
    multiple?: boolean
    loading?: boolean
    clearable?: boolean
    showTooltip?: boolean
    customTooltip?: boolean
    appendToBody?: boolean
    labelKey?: string
    placeholder?: string
    config?: ConfigType
    size?: 'large' | 'small' | 'mini'
    secondLabelKey?: string
    valueKey?: string
    value?: ValueType
    data?: Array<ObjectAnyType>
    fullWidth?: boolean
}

const props = withDefaults(
    defineProps<IProps>(),
    {
        collapseTags: true,
        filterable: true,
        appendToBody: true,
        multiple: true,
        loading: false,
        showTooltip: false,
        customTooltip: false,
        clearable: false,
        labelKey: 'label',
        secondLabelKey: '',
        placeholder: '',
        size: 'small',
        valueKey: 'value',
        value: undefined,
        config: undefined,
        data: () => []
    }
)

const emits = defineEmits([ 'change', 'input' ])

/* Computed */
const modelLocal = computed({
    get () {
        return props.value
    },
    set (val: ValueType | undefined) {
        emits('change', val)
        emits('input', val)
    }
})
const allValuesSelected = computed(() => {
    return Array.isArray(modelLocal.value) && modelLocal.value.length === props.data.length
})
const noValueSelected = computed(() => {
    return Array.isArray(modelLocal.value) && modelLocal.value.length === 0
})

/* Methods */
function composeLabel (option: any) {
    const _labelKey = props.config?.labelKey ?? props.labelKey
    const _secondLabelKey = props.config?.secondLabelKey ?? props.secondLabelKey
    if (!props.secondLabelKey) {
        return option[_labelKey]
    }
    return `${option[_labelKey]} (${option[_secondLabelKey]})`
}

function onChange (value: ValueType | undefined) {
    if (props.multiple && Array.isArray(value)) {
        if (value.includes('all')) {
            return selectAll()
        }
        if (value.includes('none')) {
            return selectNone()
        }
    }
    modelLocal.value = value
}

function selectAll () {
    const _key = props.config?.valueKey ?? props.valueKey
    const value = props.data.map((d: any) => d[_key])
    nextTick(() => {
        modelLocal.value = value
    })
}

function selectNone () {
    nextTick(() => {
        modelLocal.value = Array.isArray(props.value) ? [] : undefined
    })
}

function getOptionValue (option: any) {
    if (typeof option === 'object' && !Array.isArray(option)) {
        const _key = props.config?.valueKey ?? props.valueKey
        return option[_key]
    }
    return option
}
</script>

<style lang="scss">
.base-select {
    .el-select-dropdown.is-multiple .el-select-dropdown__item.selected {
        color: var(--primary-color) !important;
    }

    .select-label {
        @apply text-gray-950 text-sm mb-2 font-semibold;
    }

    .el-select .el-input .el-select__caret {
        @apply text-primary font-bold;
    }

    .el-input__inner::placeholder {
        @apply text-gray-500 font-normal text-sm;
    }

    .el-input__prefix {
        @apply flex items-center font-medium;
    }
}

.custom-tooltip-content {
    @apply max-w-[400px] max-h-[250px] overflow-y-auto pr-5;
}

[dir='rtl'], .rtl {
    .base-select {
        .el-select {
            .el-input--suffix .el-input__inner {
                padding-right: 4px;
                padding-left: 30px;
            }

            .el-input__suffix {
                right: auto;
                left: 5px;
            }

            .el-input__prefix {
                left: auto;
                right: 5px;
            }

            .el-select__tags {
                //flex-direction: row-reverse;
            }

            .el-tag {
                margin: 2px 6px 2px 0;
            }

            .el-icon-close {
                right: 2px;
            }

            .el-select__input {
                margin-right: 12px;
                margin-left: 4px;
            }
        }
    }
}
</style>
